import {
    Component,
    ChangeDetectionStrategy,
    Input,
    Output,
    EventEmitter,
} from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CaseInvoicing, CaseName, Client, Faktura, Fakturalinjer, KunderAdr, Settings } from '@store/models';
import { ConfirmService } from 'app/shared/confirm-dialog/confirm-dialog.service';
import { BehaviorSubject } from 'rxjs';
import { ChangeCurrencyComponent } from '../change-currency/change-currency.component';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { InvoicePdfService } from '../services/invoice-pdf.service';
import { AlertService } from 'app/core/alerts/alert.service';
pdfMake.vfs = pdfFonts.pdfMake.vfs;
import { LuxonUtils, NgbHelperService } from '@shared';

@Component({
    selector: 'app-invoice-ui',
    templateUrl: './invoice.component.html',
    styleUrls: ['./invoice.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class InvoiceComponent {
    @Input() faktura: Faktura;
    @Input() client: Client;
    @Input() createCases: CaseInvoicing[];
    @Input() caseNames: CaseName[];
    @Input() kunderAdr: KunderAdr[];
    @Input() officeSettings: Settings;
    @Input() interestRate: number;
    @Output() updateFaktura = new EventEmitter<{fnr: number, partialFaktura: Partial<Faktura>}>();
    @Output() godkendFaktura = new EventEmitter<number>();
    @Output() sendFaktura = new EventEmitter<{ faktura: Faktura, blob: Blob, fakturaEmail: string }>();
    @Output() deleteFaktura = new EventEmitter<number>();
    @Output() deleteFakturalinje = new EventEmitter<number>();
    @Output() saveFakturalinje = new EventEmitter<Fakturalinjer>();
    newFakturalinjerSubject = new BehaviorSubject<Fakturalinjer[]>([]);
    newFakturalinjer$ = this.newFakturalinjerSubject.asObservable();
    newFakturalinjer: Fakturalinjer[] = [];
    editFakturaDato = false;
    editBetalingsDato = false;
    editingFakturalinjer: number[] = [];

    get fakturaDatoDateStruct() {
        return this.ngbHelper.toNgbDateStruct(this.faktura.dato);
    }

    get fakturaDatoValue() {
        const date = LuxonUtils.fromIsoUtc(this.faktura.dato);
        return date.toFormat('dd-LL-yyyy');
    }

    get betalingsDatoValue() {
        const date = LuxonUtils.fromIsoUtc(this.faktura.betdato);
        return date.toFormat('dd-LL-yyyy');
    }

    get isEditing() {
        return this.editFakturaDato || this.editBetalingsDato || this.newFakturalinjer.length > 0 || this.editingFakturalinjer.length > 0;
    }

    constructor(
        private modal: NgbActiveModal,
        private ngbModal: NgbModal,
        private confirm: ConfirmService,
        private invoicePdf: InvoicePdfService,
        private alert: AlertService,
        private ngbHelper: NgbHelperService
    ) { }

    async onClose() {
        if (this.isEditing) {
            const closeAnyway = await this.confirm.question(
                'Du er igang med at redigere felter, som ikke vil blive gemt hvis du lukker skærmbilledet. Vil du lukke alligevel?',
                'Ja',
                'Nej'
            );
            if (!closeAnyway) { return; }
        }
        this.modal.close(this.faktura);
    }

    onKunderAdr(event: any) {
        const aid = +event.target.value;
        if (isNaN(aid)) { return; }
        if (aid === 0) {
            this.updateFaktura.emit({
                fnr: this.faktura.fnr,
                partialFaktura: {
                    navn1: this.client.navn1,
                    navn2: this.client.navn2,
                    adresse1: this.client.adresse1,
                    adresse2: this.client.adresse2,
                    postnr: this.client.postnr,
                    bynavn: this.client.bynavn,
                    land: this.client.land
                }
            });
            return;
        }
        const selectedKunderAdr = this.kunderAdr.find(ka => ka.aid === aid);
        this.updateFaktura.emit({
            fnr: this.faktura.fnr,
            partialFaktura: {
                navn1: selectedKunderAdr.navn1,
                navn2: selectedKunderAdr.navn2,
                adresse1: selectedKunderAdr.adresse,
                adresse2: '',
                postnr: selectedKunderAdr.postnr.toString(),
                bynavn: selectedKunderAdr.bynavn,
                land: selectedKunderAdr.land
            }
        });
    }

    onSaveDato(fakturaDato: string, feltnavn: 'dato' | 'betdato') {
        const newFakturaDato = LuxonUtils.fromDanishDateString(fakturaDato, 'dd-LL-yyyy');
        if (!newFakturaDato.isValid) {
            this.alert.createAlert(
                'Der er fejl i datoen!',
                'warning'
            );
            return;
        }
        this.updateFaktura.emit({
            fnr: this.faktura.fnr,
            partialFaktura: {
                [feltnavn]: newFakturaDato.toJSDate()
            }
        });
        if (feltnavn === 'dato') { this.editFakturaDato = false; }
        if (feltnavn === 'betdato') { this.editBetalingsDato = false; }
    }

    onEditFakturalinje(edit: boolean, flid: number) {
        edit
            ? this.editingFakturalinjer = [...this.editingFakturalinjer, flid]
            : this.editingFakturalinjer = this.editingFakturalinjer.filter(eflid => eflid !== flid);
    }

    onDeleteFakturalinje(flid: number) {
        this.deleteFakturalinje.emit(flid);
    }

    addNewFakturalinje() {
        this.newFakturalinjer.push({
            flid: 0,
            fid: null,
            tekst: '',
            beløb:  0,
            sag:  null,
            moms:  25,
            kontor:  null
        });
        this.newFakturalinjerSubject.next(this.newFakturalinjer);
    }

    async onChangeCurrency() {
        const currencyModal = this.ngbModal.open(ChangeCurrencyComponent, {backdrop: 'static'});
        currencyModal.componentInstance.fakturaValuta = {
            valuta: this.faktura.valuta,
            valutaKurs: this.faktura.valutaKurs
        };
        await currencyModal.result
        .then((value: Partial<Faktura>) => {
            if (!value) { return; }
            this.updateFaktura.emit({
                fnr: this.faktura.fnr,
                partialFaktura: value
            });
        });
    }

    cancelNewFakturalinje(index: number) {
        this.newFakturalinjer.splice(index, 1);
        this.newFakturalinjerSubject.next(this.newFakturalinjer);
    }

    onUpdateFakturalinje(fakturalinje: Fakturalinjer) {
        this.saveFakturalinje.emit(fakturalinje);
    }

    onCreateFakturalinje(index: number, fakturalinje: Fakturalinjer) {
        const newFakturalinje = {
            ...fakturalinje,
            kontor: this.faktura.kontor,
            fid: this.faktura.fnr
        };
        this.saveFakturalinje.emit(newFakturalinje);
        this.newFakturalinjer.splice(index, 1);
        this.newFakturalinjerSubject.next(this.newFakturalinjer);
    }

    async onGodkendFaktura() {
        if (this.isEditing) {
            this.isEditingAlert();
            return;
        }
        await this.confirm.question(
            'Vil du godkende denne? Du kan så ikke længere rette i fakturaen!',
            'Godkend',
            'Annuller'
        ).then(godkend => {
            if (godkend) {
                this.godkendFaktura.emit(this.faktura.fnr);
            }
        });
    }

    async onDeleteFaktura() {
        if (this.isEditing) {
            this.isEditingAlert();
            return;
        }
        await this.confirm.question(
            'Vil du slette denne faktura?',
            'Slet',
            'Annuller'
        ).then(slet => {
            if (slet) {
                this.deleteFaktura.emit(this.faktura.fnr);
            }
        });
    }

    onPdfPreview() {
        if (this.isEditing) {
            this.isEditingAlert();
            return;
        }
        const doc = this.invoicePdf.getPDFdoc(this.faktura, this.officeSettings, this.interestRate);
        pdfMake.createPdf(doc).open();
    }

    async onSendFaktura() {
        if (this.isEditing) {
            this.isEditingAlert();
            return;
        }
        const doc = this.invoicePdf.getPDFdoc(this.faktura, this.officeSettings, this.interestRate);
        const pdfDocGenerator = pdfMake.createPdf(doc);
        // noinspection TypeScriptValidateJSTypes
        pdfDocGenerator.getBlob((blob) => {
            this.sendFaktura.emit({
                faktura: this.faktura,
                blob,
                fakturaEmail: this.client.fakturaEmail
            });
        });
    }

    trackByFakturalinje(_index: number, fakturaLinje: Fakturalinjer): number {
      return fakturaLinje.flid;
    }

    private isEditingAlert() {
        this.alert.createAlert(
            'Handlingen kan ikke udføres mens faktura redigeres!',
            'warning'
        );
    }
}
